import { BaseEdge, getSmoothStepPath, type EdgeProps } from '@xyflow/react';
import { memo } from 'react';
import type { RefType } from '@hyphen-db-studio/schema-core';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';

const CARDINALITY: Record<RefType, [string, string]> = {
  '>': ['*', '1'],
  '<': ['1', '*'],
  '-': ['1', '1'],
  '<>': ['*', '*'],
};

export interface RelationshipEdgeData {
  type: RefType;
  highlighted?: boolean;
  [key: string]: unknown;
}

function RelationshipEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps & { data?: RelationshipEdgeData }) {
  const [path] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 12,
  });
  const [fromLabel, toLabel] = CARDINALITY[data?.type ?? '>'];
  // During the guided story: a relationship between lit-up tables glows amber, every other one fades back.
  const tour = useDiagramViewStore((s) => (s.tourFocus ? (s.tourFocus.edgeIds.has(id) ? 'focus' : 'dim') : null));
  const highlighted = !!data?.highlighted || selected;

  const strokeColor = tour === 'focus' ? '#f59e0b' : highlighted ? '#6366f1' : 'var(--edge-stroke, #94a3b8)';
  const strokeWidth = tour === 'focus' ? 3 : highlighted ? 2.5 : 1.5;

  return (
    <>
      <BaseEdge id={id} path={path} style={{ stroke: strokeColor, strokeWidth, opacity: tour === 'dim' ? 0.08 : 1, transition: 'opacity 500ms' }} />
      {(highlighted || tour === 'focus') && tour !== 'dim' && (
        <path
          d={path}
          fill="none"
          stroke={tour === 'focus' ? '#fde68a' : '#c7d2fe'}
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray="18 160"
          className="edge-shine"
          style={{ filter: 'drop-shadow(0 0 3px #818cf8)' }}
        />
      )}
      <text x={sourceX} y={sourceY - 8} textAnchor="middle" className="edge-cardinality-label" opacity={tour === 'dim' ? 0.08 : 1}>
        {fromLabel}
      </text>
      <text x={targetX} y={targetY - 8} textAnchor="middle" className="edge-cardinality-label" opacity={tour === 'dim' ? 0.08 : 1}>
        {toLabel}
      </text>
    </>
  );
}

export default memo(RelationshipEdge);
